import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import RichText from "@/components/RichText";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import { ChevronLeft, FileText, RotateCcw, Eye, History, Palette } from "lucide-react";

const FIELDS = [
  ["business_name", "Client / business name"], ["client_person_name", "Owner name (sole trader / individual)"], ["trading_name", "Trading name ('trading as')"], ["company_number", "Company number"],
  ["company_number_line", "Company no. (with brackets)"], ["year_end", "Year end"],
  ["accountant_firm", "Accountant firm"], ["accountant_contact", "Accountant contact"],
  ["accountant_address", "Accountant address"], ["services_list", "Services engaged (list)"],
  ["requirement_blocks", "Requirement blocks (per service)"], ["individuals_section", "Self Assessment individuals section"], ["owner_name", "Proposal owner"],
  ["clearance_date", "Today's date"], ["business_principal", "CASS principal"],
  ["business_name_cass", "CASS firm name"],
];

const KINDS = [
  ["company", "Limited Company / LLP / Partnership"],
  ["individual", "Sole Trader / Individual"],
];

export default function ClearanceTemplate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [kind, setKind] = useState("company");
  const [html, setHtml] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [compareWith, setCompareWith] = useState(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [brand, setBrand] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);

  const loadBranding = () => api.get("/clearance-branding").then((r) => setBrand(r.data)).catch(() => {});
  useEffect(() => { loadBranding(); }, []);

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.put("/clearance-branding", {
        show_logo: !!brand.show_logo, logo_width_mm: Number(brand.logo_width_mm) || 42,
        accent_color: brand.accent_color || "#0B1A2F", firm_name: brand.firm_name || "",
        firm_address: brand.firm_address || "", firm_email: brand.firm_email || "",
        firm_phone: brand.firm_phone || "", footer_text: brand.footer_text || "",
      });
      setBrand(data);
      toast.success("Letterhead & branding saved");
      setBrandOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save branding"); }
    finally { setSavingBrand(false); }
  };
  const setB = (k) => (e) => setBrand((b) => ({ ...b, [k]: e.target.value }));

  const loadTpl = (k) => api.get(`/clearance-template?kind=${k}`).then((r) => { setHtml(r.data.letter_html || ""); setLoaded(true); });
  useEffect(() => { setLoaded(false); loadTpl(kind); /* eslint-disable-next-line */ }, [kind]);

  const save = async () => {
    try { await api.put("/clearance-template", { letter_html: html, kind }); toast.success("Clearance template saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const insertField = (token) => setHtml((h) => h + `{{${token}}}`);

  const preview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/clearance-template/preview", { letter_html: html, kind }, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) { toast.error("Could not build preview"); }
    finally { setPreviewing(false); }
  };
  const closePreview = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(""); };

  const openHistory = async () => {
    setCompareWith(null);
    try { const r = await api.get(`/clearance-template/versions?kind=${kind}`); setVersions(r.data); setHistoryOpen(true); }
    catch { toast.error("Could not load history"); }
  };
  const restore = async (v) => {
    if (!(await confirmDialog({ title: "Restore this version?", description: "The current template will be replaced with this saved version (a new history entry is recorded). Applies to future letters only.", confirmText: "Restore" }))) return;
    try { await api.post(`/clearance-template/versions/${v.id}/restore`); toast.success("Version restored"); setHistoryOpen(false); loadTpl(kind); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };

  return (
    <div>
      <PageHeader title="Professional Clearance Letter" accent="teal" Icon={FileText}
        subtitle="Master templates used to auto-draft clearance letters. The right one is chosen by the client's business type. Per-client edits never change these masters."
        actions={<div className="flex gap-2">
          {canEdit && <Button variant="outline" size="sm" onClick={() => setBrandOpen(true)} disabled={!brand} data-testid="clt-branding"><Palette className="w-4 h-4 mr-1" />Letterhead &amp; branding</Button>}
          <Button variant="outline" size="sm" onClick={openHistory} disabled={!loaded} data-testid="clt-history"><History className="w-4 h-4 mr-1" />History</Button>
          <Button variant="outline" size="sm" onClick={preview} disabled={previewing || !loaded} data-testid="clt-preview"><Eye className="w-4 h-4 mr-1" />{previewing ? "Building…" : "Preview letter"}</Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="clt-back"><ChevronLeft className="w-4 h-4 mr-1" />Settings</Button>
        </div>} />
      <div className="p-4 md:p-8 max-w-[900px]">
        <div className="mb-5 inline-flex rounded-lg border border-cass-navy/15 bg-cass-bone/50 p-1" data-testid="clt-kind-toggle">
          {KINDS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setKind(k)} data-testid={`clt-kind-${k}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${kind === k ? "bg-cass-navy text-white" : "text-cass-slate hover:text-cass-navy"}`}>{l}</button>
          ))}
        </div>
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="clt-fields">
          <span className="text-xs text-cass-slate mr-1 self-center">Merge fields:</span>
          {FIELDS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => insertField(k)} disabled={!canEdit}
              className="text-[11px] font-mono px-2 py-1 rounded bg-cass-bone text-cass-navy hover:bg-cass-teal hover:text-white transition-colors" title={l} data-testid={`clt-field-${k}`}>{`{{${k}}}`}</button>
          ))}
        </div>
        {loaded && <RichText value={html} onChange={setHtml} testId="clt-editor" />}
        <p className="text-xs text-cass-slate mt-2">The <span className="inline-block font-mono text-[10px] bg-cass-bone rounded px-1.5 py-0.5 text-cass-navy">{"{{requirement_blocks}}"}</span> field is built from each engaged service's clearance requirement block (Service Catalogue → Edit service).{kind === "individual" && " Sole traders / individuals have no company number, so leave that merge field out of this template."}</p>
        {canEdit && (
          <div className="mt-5 flex gap-2">
            <Button className="bg-cass-teal text-white" onClick={save} data-testid="clt-save">Save template</Button>
            <Button variant="outline" onClick={() => loadTpl(kind).then(() => toast.info("Reverted to saved"))} data-testid="clt-revert"><RotateCcw className="w-4 h-4 mr-1" />Revert</Button>
          </div>
        )}
      </div>
      <Dialog open={!!previewUrl} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl h-[88vh] flex flex-col p-0" data-testid="clt-preview-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" />Letter preview <span className="text-xs font-normal text-cass-slate">(sample data)</span></DialogTitle>
            <DialogDescription className="text-xs">Rendered with sample client data so you can check the layout.</DialogDescription>
          </DialogHeader>
          {previewUrl && <iframe title="Clearance preview" src={previewUrl} className="flex-1 w-full rounded-b-md" data-testid="clt-preview-frame" />}
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="clt-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4" />Template history — {kind === "individual" ? "Individual / Sole Trader" : "Limited Company"}</DialogTitle>
            <DialogDescription>Every save is versioned. Compare a past version with the current draft, or restore it (future letters only).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="clt-version-list">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 border border-border rounded-md p-2" data-testid={`clt-version-${v.id}`}>
                <div className="text-xs text-cass-navy">{v.saved_at ? new Date(v.saved_at).toLocaleString("en-GB") : ""} · <span className="text-cass-slate">{v.saved_by}</span></div>
                <div className="flex gap-2">
                  <Button size="sm" variant={compareWith?.id === v.id ? "default" : "outline"} className={compareWith?.id === v.id ? "bg-cass-navy text-white h-7" : "h-7"} onClick={() => setCompareWith(compareWith?.id === v.id ? null : v)} data-testid={`clt-compare-${v.id}`}>Compare</Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => restore(v)} data-testid={`clt-restore-${v.id}`}>Restore</Button>
                </div>
              </div>
            ))}
            {versions.length === 0 && <p className="text-sm text-cass-slate">No versions yet.</p>}
          </div>
          {compareWith && (
            <div className="grid grid-cols-2 gap-3 mt-3 border-t border-border pt-3" data-testid="clt-compare-view">
              <div><div className="text-xs font-semibold text-cass-navy mb-1">Current draft</div><div className="prose prose-sm max-w-none border border-border rounded-md p-3 bg-white max-h-[45vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: html }} /></div>
              <div><div className="text-xs font-semibold text-cass-navy mb-1">Selected version</div><div className="prose prose-sm max-w-none border border-border rounded-md p-3 bg-cass-bone/30 max-h-[45vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: compareWith.letter_html }} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={brandOpen} onOpenChange={setBrandOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="clt-branding-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Palette className="w-4 h-4" />Letterhead &amp; branding</DialogTitle>
            <DialogDescription>Applies to the PDF letterhead for <b>both</b> letter types (company &amp; individual). The letter wording itself is edited above.</DialogDescription>
          </DialogHeader>
          {brand && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs">Show logo</Label>
                  <p className="text-[11px] text-cass-slate">{brand.logo_available ? "Uses your default brand logo." : "No brand logo uploaded — set one under Branding settings."}</p>
                </div>
                <Switch checked={!!brand.show_logo} onCheckedChange={(v) => setBrand((b) => ({ ...b, show_logo: v }))} data-testid="clt-brand-show-logo" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Logo width (mm)</Label>
                  <Input type="number" min={10} max={90} value={brand.logo_width_mm ?? 42} onChange={setB("logo_width_mm")} disabled={!brand.show_logo} data-testid="clt-brand-logo-width" />
                </div>
                <div>
                  <Label className="text-xs">Accent colour</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={brand.accent_color || "#0B1A2F"} onChange={setB("accent_color")} className="h-9 w-12 rounded border border-border p-0.5" data-testid="clt-brand-accent" />
                    <Input value={brand.accent_color || "#0B1A2F"} onChange={setB("accent_color")} className="font-mono text-xs" />
                  </div>
                </div>
              </div>
              <div><Label className="text-xs">Firm name (letterhead)</Label><Input value={brand.firm_name || ""} onChange={setB("firm_name")} data-testid="clt-brand-firm-name" /></div>
              <div><Label className="text-xs">Firm address</Label><Input value={brand.firm_address || ""} onChange={setB("firm_address")} data-testid="clt-brand-firm-address" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Firm email</Label><Input value={brand.firm_email || ""} onChange={setB("firm_email")} data-testid="clt-brand-firm-email" /></div>
                <div><Label className="text-xs">Firm phone</Label><Input value={brand.firm_phone || ""} onChange={setB("firm_phone")} data-testid="clt-brand-firm-phone" /></div>
              </div>
              <div><Label className="text-xs">Footer (small print)</Label><Input value={brand.footer_text || ""} onChange={setB("footer_text")} placeholder="e.g. Registered in England · ICAEW no. 12345 · www.cass-online.co.uk" data-testid="clt-brand-footer" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBrandOpen(false); loadBranding(); }}>Cancel</Button>
            <Button className="bg-cass-teal text-white" onClick={saveBranding} disabled={savingBrand || !brand} data-testid="clt-brand-save">Save branding</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
